import { useState } from "react";
import toast from "react-hot-toast";
import { UserPlus } from "lucide-react";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { Button } from "@/components/Button";
import { FormField } from "@/components/FormField";
import { leadsService, Lead } from "@/services/leads.service";

export function LeadCreateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (lead: Lead) => void;
}) {
  const [form, setForm] = useState({
    profileName: "",
    phone: "",
    email: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const close = () => {
    if (!busy) onClose();
  };
  return (
    <Modal open onClose={close} title="Add lead">
      <form
        className="space-y-4"
        onSubmit={async (event) => {
          event.preventDefault();
          if (busy) return;
          setBusy(true);
          try {
            const result = await leadsService.create({
              profileName: form.profileName.trim() || undefined,
              phone: form.phone.trim() || undefined,
              email: form.email.trim() || undefined,
              notes: form.notes.trim() || undefined,
            });
            toast.success(
              result.created
                ? "Lead created and linked to a contact"
                : "An existing lead was found",
            );
            onCreated(result.lead);
          } catch (error: any) {
            if (!error?.response) toast.error("Could not save this lead");
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className="flex items-start gap-3 rounded-xl border border-border bg-surface-alt p-3">
          <span className="rounded-lg bg-primary-soft p-2 text-primary">
            <UserPlus size={18} />
          </span>
          <p className="text-xs leading-relaxed text-text-secondary">
            Add a phone number or email. Matching customers are linked to their
            existing contact.
          </p>
        </div>
        <FormField label="Customer name">
          <Input
            autoFocus
            maxLength={200}
            value={form.profileName}
            onChange={(event) =>
              setForm({ ...form, profileName: event.target.value })
            }
            placeholder="Full name"
          />
        </FormField>
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="Phone">
            <Input
              type="tel"
              maxLength={80}
              value={form.phone}
              onChange={(event) =>
                setForm({ ...form, phone: event.target.value })
              }
              placeholder="+971 50 123 4567"
            />
          </FormField>
          <FormField label="Email">
            <Input
              type="email"
              maxLength={254}
              value={form.email}
              onChange={(event) =>
                setForm({ ...form, email: event.target.value })
              }
              placeholder="name@company.com"
            />
          </FormField>
        </div>
        <FormField label="Notes">
          <textarea
            maxLength={5000}
            rows={3}
            value={form.notes}
            onChange={(event) =>
              setForm({ ...form, notes: event.target.value })
            }
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary-soft"
            placeholder="What is this customer interested in?"
          />
        </FormField>
        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={close}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={busy}
            disabled={!form.phone.trim() && !form.email.trim()}
          >
            Create lead
          </Button>
        </div>
      </form>
    </Modal>
  );
}
